import { useState } from "react";
import moment from "moment";
import { PlayerPick, TeamFixture } from "../models";
import { allStats, minutesInFixture, statChips, statLabel } from "../scoring";
import "./PlayerRow.css";

interface PlayerRowProps {
	player: PlayerPick;
	/** True when the row sits in the away column and should mirror. */
	reversed?: boolean;
}

const positionClass = (player: PlayerPick) => `pos-${player.position.toLowerCase()}`;

/** One line per game: minutes played (or kick-off) and the score. */
const GameLine = ({ player, game }: { player: PlayerPick; game: TeamFixture }) => {
	if (!game.started) {
		return (
			<div className="player-game mono">
				<span className="player-status">{moment(game.kickoff).format("ddd HH:mm")}</span>
				<span className="player-game-score">{game.shortReport}</span>
			</div>
		);
	}
	const live = !game.finished;
	return (
		<div className="player-game mono">
			<span className={live ? "player-status live" : "player-status"}>
				{live && <span className="live-dot" />}
				{minutesInFixture(player, game.fixtureId)}′
			</span>
			<span className="player-game-score">{game.shortReport}</span>
		</div>
	);
};

export const PlayerRow = ({ player, reversed = false }: PlayerRowProps) => {
	const [open, setOpen] = useState(false);
	const benched = player.active !== 1;
	const chips = statChips(allStats(player));
	const subbedOn = !benched && player.pickPosition > 11;
	const subbedOff = benched && player.pickPosition <= 11;

	return (
		<div className={`player-row${reversed ? " reversed" : ""}${benched ? " benched" : ""}${open ? " open" : ""}`}>
			<button type="button" className="player-row-main" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
				<div className="player-row-text">
					<div className="player-row-name">
						<span className={`pos-badge ${positionClass(player)}`}>{player.position}</span>
						<span className="player-name">{player.name}</span>
						{player.captain && <span className="player-captain">C</span>}
						<span className="player-team mono">{player.teamShort}</span>
					</div>
					{player.games.length === 0 && <div className="player-game mono faint">No fixture</div>}
					{player.games.map((g) => (
						<GameLine key={g.fixtureId} player={player} game={g} />
					))}
					{(subbedOn || subbedOff) && <div className={`player-sub mono${subbedOff ? " off" : ""}`}>{subbedOn ? "SUB ON" : "SUB OFF"}</div>}
					{chips.length > 0 && <div className="player-row-chips">{chips.join(" · ")}</div>}
				</div>
				<div className="player-row-points mono">{player.points}</div>
			</button>
			{open && (
				<div className="player-row-detail">
					{player.games.map((g) => {
						const fixtureStats = (player.data ?? []).find((d) => d.fixture === g.fixtureId)?.stats ?? [];
						const scoring = fixtureStats.filter((s) => s.points !== 0);
						return (
							<div key={g.fixtureId} className="player-row-game">
								<div className="player-row-report">{g.matchReport || (g.started ? "" : `Kicks off ${moment(g.kickoff).format("ddd HH:mm")}`)}</div>
								{scoring.length === 0 ? (
									<div className="player-row-stat faint">No points yet</div>
								) : (
									scoring.map((s) => (
										<div key={s.identifier} className="player-row-stat">
											<span>
												{statLabel(s.identifier)} ({s.value})
											</span>
											<span className={s.points > 0 ? "mono good" : "mono bad"}>{s.points > 0 ? `+${s.points}` : s.points}</span>
										</div>
									))
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
